import { useEffect, useRef, useState } from "react";
import { FileText, Film, Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { useWizard } from "./WizardContext";
import { KINDS, checkFile, uploadFile, removeFile } from "../../lib/upload";
import { supabase } from "../../lib/supabase";

const localPreviews = new Map();

function Preview({ item, onRemove, testId }) {
  const [url, setUrl] = useState(localPreviews.get(item.path) || null);
  useEffect(() => {
    if (url || item.bucket !== "profile-photos" || !item.type?.startsWith("image/")) return;
    supabase.storage.from(item.bucket).createSignedUrl(item.path, 3600).then(({ data }) => data && setUrl(data.signedUrl));
  }, [item, url]);
  const Icon = item.type?.startsWith("video/") ? Film : FileText;
  return (
    <div data-testid={testId} className="relative h-24 w-24 overflow-hidden rounded-lg border border-[#D4AF37]/50 bg-[#F4EFEA]">
      {url && item.type?.startsWith("image/") ? (
        <img src={url} alt={item.name} className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full flex-col items-center justify-center gap-1 p-1 text-center">
          <Icon className="h-6 w-6 text-[#7A0016]" />
          <span className="line-clamp-2 break-all text-[10px] text-[#8C6A6C]">{item.name}</span>
        </div>
      )}
      <button type="button" onClick={onRemove} data-testid={`${testId}-remove`} aria-label="Remove file" className="absolute right-1 top-1 rounded-full bg-white/90 p-1 text-[#7A0016] shadow hover:bg-white">
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

export function UploadTile({ field, label, kind, bucket, folder, multiple = false, max = 1, required, testId }) {
  const { data, set, errors, userId } = useWizard();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const value = data.uploads[field];
  const items = multiple ? value : value ? [value] : [];
  const error = errors[`uploads.${field}`];

  const onFiles = async (fileList) => {
    if (multiple && items.length + fileList.length > max) toast.error(`Maximum ${max} photos allowed`);
    const files = Array.from(fileList).slice(0, Math.max(0, max - (multiple ? items.length : 0)) || 1);
    setBusy(true);
    const uploaded = [];
    for (const file of files) {
      const problem = checkFile(file, kind);
      if (problem) { toast.error(`${file.name}: ${problem}`); continue; }
      try {
        const item = await uploadFile({ file, kind, bucket, folder, userId });
        if (file.type.startsWith("image/")) localPreviews.set(item.path, URL.createObjectURL(file));
        uploaded.push(item);
      } catch (e) {
        toast.error(`Upload failed: ${e.message}`);
      }
    }
    if (uploaded.length) {
      if (multiple) set("uploads", field, [...items, ...uploaded].slice(0, max));
      else { if (value) removeFile(value); set("uploads", field, uploaded[0]); }
    }
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = (item) => {
    removeFile(item);
    set("uploads", field, multiple ? items.filter((i) => i.path !== item.path) : null);
  };

  const canAdd = multiple ? items.length < max : true;
  return (
    <div data-error={error ? "true" : undefined} className="space-y-2">
      <p className="text-sm font-medium text-[#2C2424]">{label} {required && <span className="text-[#A31828]">*</span>}</p>
      <div className="flex flex-wrap gap-3">
        {items.map((it, i) => <Preview key={it.path} item={it} onRemove={() => remove(it)} testId={`${testId}-preview-${i}`} />)}
        {canAdd && (
          <button type="button" disabled={busy} onClick={() => inputRef.current?.click()} data-testid={`${testId}-button`} aria-label={value && !multiple ? "Replace file" : "Add file"}
            className={`flex h-24 w-24 flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed text-xs transition-colors ${error ? "border-[#A31828]" : "border-[#D4AF37]/70"} bg-white text-[#7A0016] hover:bg-[#F5E6BA]/40`}>
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Plus className="h-5 w-5" />}
            <span className="text-[10px] font-semibold">{busy ? "Uploading" : value && !multiple ? "Replace" : "Add"}</span>
          </button>
        )}
      </div>
      <p className="text-[10px] text-[#8C6A6C]">{KINDS[kind].hint}{multiple ? ` (maximum ${max})` : ""}</p>
      <input ref={inputRef} type="file" hidden multiple={multiple} accept={KINDS[kind].types.join(",")} data-testid={`${testId}-input`} onChange={(e) => e.target.files?.length && onFiles(e.target.files)} />
      {error && <p data-testid={`error-uploads-${field}`} className="text-xs font-medium text-[#A31828]">{error}</p>}
    </div>
  );
}
